import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { fetchDentallyPractitioners } from '../../utils/dentallyApi';
import { hasFeatureAccess } from '../../utils/featurePermission';

// Read cached practitioners from localStorage for a given practice
function hydrateFromCache(practiceId) {
  try {
    const raw = localStorage.getItem(`dentallyPractitioners_${practiceId}`);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore parse errors or disabled localStorage
  }
  return [];
}

const initialState = {
  practitioners: [],
  isLoading: false,
  isLoaded: false,
  practiceId: null,
};

const slice = createSlice({
  name: 'dentallyPractitioners',
  initialState,
  reducers: {
    hydrateCache(state, action) {
      if (!state.isLoaded) {
        state.practitioners = action.payload.practitioners;
        state.practiceId = action.payload.practiceId;
      }
    },
    setLoading(state, action) {
      state.isLoading = action.payload;
    },
    setPractitioners(state, action) {
      state.practitioners = action.payload.practitioners;
      state.practiceId = action.payload.practiceId;
      state.isLoading = false;
      state.isLoaded = true;
    },
    reset(state) {
      state.practitioners = [];
      state.isLoading = false;
      state.isLoaded = false;
      state.practiceId = null;
    },
  },
});

export default slice.reducer;

export const { reset: resetDentallyPractitioners } = slice.actions;

// Prefetch all practitioners: hydrate from localStorage immediately, then background-fetch from API
export function prefetchDentallyPractitioners(practiceId) {
  return async () => {
    if (!practiceId) return;

    // Skip unless dentally-integration is explicitly 'view' or 'manage'.
    // Catches hidden, undefined (permissions not loaded yet), and any other state.
    if (!hasFeatureAccess('dentally-integration')) return;

    // Step 1: Instant hydrate from localStorage
    const cached = hydrateFromCache(practiceId);
    if (cached.length > 0) {
      dispatch(slice.actions.hydrateCache({ practitioners: cached, practiceId }));
    }

    // Step 2: Background API fetch (always runs to get fresh data)
    dispatch(slice.actions.setLoading(true));
    try {
      const all = await fetchDentallyPractitioners(practiceId);
      dispatch(slice.actions.setPractitioners({ practitioners: all, practiceId }));
      // Persist to localStorage for next load
      try {
        localStorage.setItem(`dentallyPractitioners_${practiceId}`, JSON.stringify(all));
      } catch {
        // quota exceeded or disabled — ignore
      }
    } catch (err) {
      dispatch(slice.actions.setLoading(false));
      console.error('Failed to prefetch Dentally practitioners:', err);
    }
  };
}
